// src/gen/difficulty.ts
// §3.4 difficulty bands + resolveDifficulty. Pure function of (levelIndex, seed).

import type { Rng } from './prng';
import { nextRange } from './prng';
import type { ShapeId } from '../game/shapes';

/**
 * Max packing density the constructive spawner can reliably reach. Shared with generator.ts
 * (which imports it) so both stay in sync. See generator.ts SHAPE_PACK_DENSITY for full rationale.
 */
export const SHAPE_PACK_DENSITY = 0.90;

/**
 * Fixed playfield (FIXED-BOARD re-tune, matches the reference image). EVERY level is a portrait
 * 5-wide × 7-tall board — no longer square, no longer growing with the level index. Difficulty now
 * scales purely through piece count + scramble depth. Exported so the generator and tests share one
 * source of truth.
 */
export const BOARD_COLS = 5;
export const BOARD_ROWS = 7;

// Shape pools widen monotonically as levelIndex increases (pure function of levelIndex, no rng).
// REFERENCE-LOOK re-tune: pools are THIN BARS + small L/J/T BENDS only — no fat 2x2 squares. The
// chunky squares `sq` and `oTet` remain in the SHAPES catalog but are intentionally excluded from
// every pool, so generated boards read as lots of slim, interlocking pieces (1-wide bars wedged
// around L/T bends) rather than a few blobby shapes. `dot` is fine as filler.
const POOL_TINY: readonly ShapeId[] = ['dot', 'duo'];
const POOL_TRI: readonly ShapeId[] = [...POOL_TINY, 'tri', 'lTri', 'jTri'];
const POOL_TET_BASIC: readonly ShapeId[] = [...POOL_TRI, 'iBar', 'tTet', 'lTet', 'jTet'];
const POOL_FULL: readonly ShapeId[] = [...POOL_TET_BASIC];

/**
 * Returns the shape pool for a given 1-based level index. Pure: no rng, no side effects.
 * Pools are monotonically widening supersets as levelIndex grows.
 *
 * FIXED-BOARD re-tune: the prominent 1x4 bar (`iBar`) enters at L8 (was L10). On the fixed 5x7
 * board the piece count climbs fast and the per-block area budget collapses to <=3 once the board
 * is busy, so the only window where a long bar still fits is the handful of levels right after it
 * becomes pool-eligible — pulling it in two levels earlier keeps the reference's prominent bars
 * genuinely present before the dense plateau squeezes them out.
 */
export function shapePoolForLevel(levelIndex: number): readonly ShapeId[] {
  if (levelIndex < 4) return POOL_TINY;
  if (levelIndex < 8) return POOL_TRI;
  if (levelIndex < 40) return POOL_TET_BASIC;
  return POOL_FULL;
}

export type BandName = 'intro' | 'easy' | 'gentlePlus' | 'variety';

/** Inclusive integer range. min === max means fixed. */
export interface IntRange {
  readonly min: number;
  readonly max: number;
}

export interface DifficultyBand {
  readonly name: BandName;
  readonly minLevel: number; // 1-based inclusive lower bound
  readonly maxLevel: number | null; // inclusive upper bound; null = open-ended.
  readonly boardSize: IntRange; // VESTIGIAL: board is now a fixed 5×7 (BOARD_COLS×BOARD_ROWS); kept for band-table shape only
  readonly colors: IntRange; // count of distinct gate colors
  readonly blocks: IntRange; // count of blocks to spawn
  readonly scrambleMoves: IntRange; // reverse-slide count
  readonly allowHay: boolean; // §4.1 hay permitted in this band
  readonly hayChance: number; // 0..1 prob a spawned block is given cover (layered by obstacles.ts)
}

/** Concrete §3.4 bands, LOCKED. Contiguous, ascending, last is open-ended. */
export const DIFFICULTY_BANDS: readonly DifficultyBand[] = [
  {
    name: 'intro',
    minLevel: 1,
    maxLevel: 15,
    boardSize: { min: 4, max: 4 },
    colors: { min: 2, max: 2 },
    blocks: { min: 2, max: 3 },
    scrambleMoves: { min: 0, max: 2 },
    allowHay: false,
    hayChance: 0,
  },
  {
    name: 'easy',
    minLevel: 16,
    maxLevel: 60,
    boardSize: { min: 5, max: 5 },
    colors: { min: 2, max: 3 },
    blocks: { min: 3, max: 4 },
    scrambleMoves: { min: 2, max: 5 },
    allowHay: false,
    hayChance: 0,
  },
  {
    name: 'gentlePlus',
    minLevel: 61,
    maxLevel: 150,
    boardSize: { min: 5, max: 6 },
    colors: { min: 3, max: 3 },
    blocks: { min: 4, max: 5 },
    scrambleMoves: { min: 4, max: 8 },
    allowHay: true,
    hayChance: 0.15,
  },
  {
    name: 'variety',
    minLevel: 151,
    maxLevel: null,
    boardSize: { min: 6, max: 6 },
    colors: { min: 3, max: 4 },
    blocks: { min: 5, max: 6 },
    scrambleMoves: { min: 6, max: 10 },
    allowHay: true,
    hayChance: 0.25,
  },
];

/**
 * levelIndex is 1-based. Returns the band whose [minLevel, maxLevel] contains it. Total function:
 * never throws for levelIndex >= 1 (the 'variety' band absorbs everything >= 151).
 */
export function bandForLevel(levelIndex: number): DifficultyBand {
  for (const band of DIFFICULTY_BANDS) {
    const aboveMin = levelIndex >= band.minLevel;
    const belowMax = band.maxLevel === null || levelIndex <= band.maxLevel;
    if (aboveMin && belowMax) {
      return band;
    }
  }
  // Unreachable for levelIndex >= 1; the open-ended last band always matches. Fall back to it.
  const last = DIFFICULTY_BANDS[DIFFICULTY_BANDS.length - 1];
  if (last === undefined) {
    throw new Error('DIFFICULTY_BANDS must not be empty');
  }
  return last;
}

/** Concrete per-level parameters after resolving ranges against the seeded rng. */
export interface ResolvedDifficulty {
  readonly band: BandName;
  readonly rows: number; // fixed BOARD_ROWS (7) — portrait, no longer square
  readonly cols: number; // fixed BOARD_COLS (5)
  readonly colorCount: number;
  readonly blockCount: number;
  readonly scrambleMoves: number;
  readonly allowHay: boolean;
  readonly hayChance: number;
  /** Shapes eligible to spawn at this level. Pure function of levelIndex; never draws rng. */
  readonly shapePool: readonly ShapeId[];
}

/**
 * Resolves concrete difficulty for `levelIndex` using `rng`. Grid, colors, and blockCount are
 * derived deterministically from the level index (no rng); exactly ONE rng draw is made —
 * the scramble jitter (nextRange(rng, 0, 2)) — keeping consecutive early levels visibly different.
 */
export function resolveDifficulty(levelIndex: number, rng: Rng): ResolvedDifficulty {
  const L = Math.max(1, levelIndex);
  const band = bandForLevel(L);
  // FIXED-BOARD re-tune. The board is now a constant portrait 5×7 (area 35) for EVERY level — it does
  // not grow and it is not square. Difficulty scales purely through (a) the piece count and (b) the
  // scramble depth. A single rng draw (scramble jitter) still keeps consecutive early levels visibly
  // different. Hay still comes from the band.
  const cols = BOARD_COLS; // 5
  const rows = BOARD_ROWS; // 7
  const area = rows * cols; // 35
  // PIECE-COUNT ramp on the fixed 35-cell board. blockCount climbs ~0.55/level so the very first
  // levels stay gentle for a 5-year-old (L1=2, L2-3=3 pieces) and the board is BUSY by the low teens
  // (~8 by L11, ~10 by L15). congestCap = floor(0.30 * 35) = 10 is the densest count the constructive
  // spawner reaches reliably: the per-block budget is maxArea = floor(SHAPE_PACK_DENSITY * area /
  // blockCount), and at 10 pieces that is floor(0.90*35/10) = 3, so the plateau stays a varied mix of
  // 1x2 / 1x3 bars + small L/T bends (size <=3) rather than collapsing to all dominoes — a wall of
  // slim interlocking pieces with a little open field to maneuver. Just below the plateau (blockCount
  // <= 7, i.e. L8..L10 once `iBar` is pool-eligible) the budget rises to >=4 and the prominent 1x4
  // bars from the reference still fit. Verified solvable across 1..500 (and the 2400-seed proof
  // suite) with comfortable retry headroom (cap 64).
  // congestCap = floor(0.21 * 35) = 7. Lower than before ON PURPOSE: at 7 blocks the per-block budget
  // maxArea = floor(0.90 * 35 / 7) = 4, so the size-4 L/J/T tetromino BENDS the owner wants actually
  // fit (at 10 blocks maxArea was 3 → only bars + small L-trominoes). 7 bent pieces still pack a busy,
  // interlocking 5×7 (~70% fill) — bends jam together far more than straight bars do.
  const congestCap = Math.max(2, Math.floor(0.21 * area)); // 7 on a 5×7 board
  const blockCount = clampInt(2 + Math.floor((L - 1) * 0.55), 2, congestCap);
  // COLOR SPREAD. The palette has 6 colors; we ramp the distinct-color budget up to all 6 so busy
  // boards read as a balanced rainbow rather than a single dominant color. colorCount is clamped to
  // blockCount (cannot use more colors than there are pieces), and the spawn loop (generator.ts)
  // balances pieces across the chosen colors so no one color dominates. Early levels (few pieces)
  // use 2 colors; by ~L13 the full 6 are in play once enough pieces exist to carry them.
  const colorPalette = clampInt(2 + Math.floor((L - 1) / 3), 2, 6);
  const colorCount = Math.min(colorPalette, blockCount);
  // Deeper scramble = start state further from solved, more tangled. On dense boards many
  // reverse-slides find no room and are skipped, so congestion self-limits par; the higher ceiling
  // mainly tangles the mid-density levels.
  const scrambleMoves = clampInt(3 + Math.floor((L - 1) * 0.6) + nextRange(rng, 0, 2), 3, 24);
  return {
    band: band.name,
    rows,
    cols,
    colorCount,
    blockCount,
    scrambleMoves,
    allowHay: band.allowHay,
    hayChance: band.hayChance,
    shapePool: shapePoolForLevel(L),
  };
}

/** Clamp `v` to an integer in [lo, hi]. */
function clampInt(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, Math.round(v)));
}
